import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { MessageLogData, HttpLogData, LogRequestData, LogRequestStatus, KeyValuePair, PaginationDataSet, MessageLogCaller }
    from '@moderepo/mode-internal-apis';
import { KVPairsTableDataItem, capitalizeAll, capitalizeFirst, Time, SelectInputOption, compareObjects, compareNumbers,
    compareStrings, compareTimestamps, MemorySize, MemorySizeUnit, roundValue, TimeUnit } from '@moderepo/mode-app-components';
import { MessageLogsTableDataItem, HttpLogsTableDataItem, LogRequestEntryTableData, LogRequestAction, getLogRequesterTypeName, getLogLevelName,
    getLogRequestStatusName, getRequestMethodName } from '@moderepo/mode-app-components-internal';
import { SortOrder } from '@moderepo/mode-apis';



/**
 * This is where we define interfaces, enums, functions, or anything that are shared by containers.
 */


/**
 * Get elapsed timestamp display e.g.
 *      - 3 days ago
 *      - 12 hours ago
 *      - Jan 19, 2021, 20:09:03 PST        if (fullDateTimeFormat is true)
 *      - J07/13/21, 20:08:42 PDT           if (fullDateTimeFormat is false)
 */
export const getDateTimeAndElapsedTime = (
    timestamp: string | number | undefined, trans: TFunction, includeDateTime: boolean = true, fullDateTimeFormat: boolean = true,
) => {
    if (timestamp !== undefined) {
        if (includeDateTime) {
            const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
            const timeZoneAbbrev = moment(timestamp).tz(timeZone).format('z');
            return trans('label.timestamp_and_time_ago_display', {
                timestamp: trans(fullDateTimeFormat ? '{{date, date-format-full}}' : '{{date, date-format-compact}}', { date: timestamp }),
                time_zone: timeZoneAbbrev,
                time_ago : moment(timestamp).fromNow(),
            });
        }
        return moment(timestamp).fromNow();
    }

    return capitalizeFirst(trans('label.none'));
};



/**
 * Get timestamp display e.g.
 *      - Jan 19, 2021, 20:09:03 PST        if (fullDateTimeFormat is true)
 *      - 07/13/21, 20:08:42 PDT            if (fullDateTimeFormat is false)
 */
export const getDateTimeDisplay = (
    timestamp: string | number | undefined, trans: TFunction, fullDateTimeFormat: boolean = true,
) => {
    if (timestamp !== undefined) {
        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
        const timeZoneAbbrev = moment(timestamp).tz(timeZone).format('z');
        return trans('label.timestamp_display', {
            timestamp: trans(fullDateTimeFormat ? '{{date, date-format-full}}' : '{{date, date-format-compact}}', { date: timestamp }),
            time_zone: timeZoneAbbrev,
        });
    }

    return capitalizeFirst(trans('label.none'));
};



/**
 * Get the caller's name. First, we will try to get caller's "displayName". If displayName is not available, we will use caller's callerType + id
 * as caller name. If the caller is sudoer, we will add "*" to the end of the name.
 * @param caller
 * @param trans
 */
const getMessageLogCallerName = (caller: MessageLogCaller | undefined, trans: TFunction): string => {
    if (caller) {
        const displayName = caller.displayName
            ? caller.displayName
            : `${capitalizeAll(trans(getLogRequesterTypeName(caller.callerType)))} ${caller.id}`;

        return `${displayName}${caller.sudoer ? ' *' : ''}`;
    }

    return capitalizeAll(trans('label.none'));
};



/**
 * Log display component does not know how to translate the data so before we pass the log entries to
 * the log display component, we need to format/translate all the data.
 */
export const localizeMessageLogEntries = (logEntries: MessageLogData[] | undefined, trans: TFunction): MessageLogsTableDataItem[] | undefined => {
    if (logEntries) {
        return logEntries.map((entry: MessageLogData): MessageLogsTableDataItem => {
            const caller = getMessageLogCallerName(entry.caller, trans);

            return {
                actualValue : entry,
                displayValue: {
                    timestamp: getDateTimeDisplay(entry.timestamp, trans),
                    level    : trans(getLogLevelName(entry.level)),
                    tags     : entry.tags ? entry.tags.map((tag) => { return `${tag.type}:${tag.id}`; }).join(', ') : '',
                    caller,
                    sudoer   : entry.caller ? entry.caller.sudoer : '',
                    message  : entry.text,
                },
                displayValueTooltip: {
                    caller: entry.caller && entry.caller.sudoer ? entry.caller.sudoer : undefined,
                },
            } as MessageLogsTableDataItem;
        });
    }
    return undefined;
};


/**
 * Log display component does not know how to translate the data so before we pass the log entries to
 * the log display component, we need to format/translate all the data.
 */
export const localizeHttpLogEntries = (logEntries: HttpLogData[] | undefined, trans: TFunction): HttpLogsTableDataItem[] | undefined => {
    if (logEntries) {
        return logEntries.map((entry: HttpLogData): HttpLogsTableDataItem => {
            return {
                actualValue : entry,
                displayValue: {
                    timestamp : getDateTimeDisplay(entry.timestamp, trans),
                    clientIp  : entry.clientIp,
                    method    : trans(getRequestMethodName(entry.method)),
                    statusCode: entry.statusCode.toString(),
                    path      : entry.path,
                    latency   : (entry.latency / 1000000).toFixed(3).toString(),
                },
            } as HttpLogsTableDataItem;
        });
    }
    return undefined;
};



/**
 * Log display component does not know how to translate the data so before we pass the log entries to
 * the log display component, we need to format/translate all the data.
 */
export const localizeLogRequestEntries = (
    logEntries: readonly LogRequestData[] | undefined, trans: TFunction,
): LogRequestEntryTableData[] | undefined => {
    if (logEntries) {
        return logEntries.map((entry: LogRequestData): LogRequestEntryTableData => {
            const action = Object.values(LogRequestAction).find((a: LogRequestAction): boolean => {
                if (a === LogRequestAction.CANCEL && entry.status === LogRequestStatus.REQUESTED) {
                    return true;
                }
                if (a === LogRequestAction.DOWNLOAD && entry.status === LogRequestStatus.SUCCESS && entry.url) {
                    return true;
                }
                if (a === LogRequestAction.RELOAD_STATUS && entry.status === LogRequestStatus.UPLOADING) {
                    return true;
                }

                return false;
            });
            return {
                actualValue : entry,
                displayValue: {
                    timestamp: trans('{{date, date-format-full}}', { date: entry.ts }),
                    startDate: entry.filter.start
                        ? trans('{{date, date-format-full}}', { date: entry.filter.start }) : capitalizeAll(trans('label.none')),
                    endDate: entry.filter.end
                        ? trans('{{date, date-format-full}}', { date: entry.filter.end }) : capitalizeAll(trans('label.none')),
                    prefixes: entry.filter.prefixes ? entry.filter.prefixes.join(', ') : capitalizeAll(trans('label.none')),
                    status  : capitalizeAll(trans(getLogRequestStatusName(entry.status))),
                },
                action,
            } as LogRequestEntryTableData;
        });
    }
    return undefined;
};



// Sort the message log entries by the current sort field and order
export const sortMessageLogEntries = (logEntries: readonly MessageLogData[] | undefined, sortField: string, sortOrder: SortOrder) => {
    // The mapping of fieldName => compareFunction. The compareObjects function doesn't know the value type of the field therefore
    // we need to tell it which function to use.
    const compareFunctionMapping = {
        level    : compareNumbers,
        timestamp: compareTimestamps,
    };

    return logEntries ? [...logEntries].sort((entry1: MessageLogData, entry2: MessageLogData): number => {

        const ascCompareResult = compareObjects(entry1, entry2, sortField, compareFunctionMapping);

        // Compare the 1 data using ASCENDING order and then multiply the result with -1 if the sort order is DESC
        return ascCompareResult * (sortOrder === SortOrder.DESC ? -1 : 1);
    }) : undefined;
};



// Sort the HTTP log entries by the current sort field and order
export const sortHttpEntries = (logEntries: readonly HttpLogData[] | undefined, sortField: string, sortOrder: SortOrder) => {
    // The mapping of fieldName => compareFunction. The compareObjects function doesn't know the value type of the field therefore
    // we need to tell it which function to use.
    const compareFunctionMapping = {
        statusCode: compareNumbers,
        latency   : compareNumbers,
        clientIp  : compareStrings,
        path      : compareStrings,
        method    : compareStrings,
        timestamp : compareTimestamps,
    };

    return logEntries ? [...logEntries].sort((entry1: HttpLogData, entry2: HttpLogData): number => {

        const ascCompareResult = compareObjects(entry1, entry2, sortField, compareFunctionMapping);

        // Compare the 1 data using ASCENDING order and then multiply the result with -1 if the sort order is DESC
        return ascCompareResult * (sortOrder === SortOrder.DESC ? -1 : 1);
    }) : undefined;
};


// Sort the log requests entries by the current sort field and order
export const sortLogRequestEntries = (
    logEntries: PaginationDataSet<LogRequestData> | undefined, sortField: string, sortOrder: SortOrder,
): PaginationDataSet<LogRequestData> | undefined => {
    // The mapping of fieldName => compareFunction. The compareObjects function doesn't know the value type of the field therefore
    // we need to tell it which function to use.
    const compareFunctionMapping = {
        projectId  : compareNumbers,
        deviceId   : compareNumbers,
        contentType: compareStrings,
        filename   : compareStrings,
        id         : compareStrings,
        status     : compareStrings,
        prefixes   : compareStrings,
        token      : compareStrings,
        url        : compareStrings,
        ts         : compareTimestamps,
        startDate  : compareTimestamps,
        endDate    : compareTimestamps,
    };

    return logEntries ? {
        ...logEntries,
        items: [...logEntries.items].sort((entry1: LogRequestData, entry2: LogRequestData): number => {

            const ascCompareResult = compareObjects(entry1, entry2, sortField, compareFunctionMapping);

            // Compare the 1 data using ASCENDING order and then multiply the result with -1 if the sort order is DESC
            return ascCompareResult * (sortOrder === SortOrder.DESC ? -1 : 1);
        }),
    } : undefined;
};



/**
 * Localize the key/value pair data before passing it to the KeyValuePair Component for display
 */
export const localizeKeyValuePairData = (
    kvPairs: readonly KeyValuePair[] | undefined, trans: TFunction, selectedKVPairKey: string | undefined,
): readonly KVPairsTableDataItem[] | undefined => {
    if (kvPairs) {
        return kvPairs.map((kvPair: KeyValuePair): KVPairsTableDataItem => {
            const value = kvPair.value ? JSON.stringify(kvPair.value) : '';
            return {
                actualValue : kvPair,
                displayValue: {
                    key             : kvPair.key,
                    value           : value.length > 500 ? `${value.substring(0, 500)}...` : value,
                    modificationTime: getDateTimeAndElapsedTime(kvPair.modificationTime, trans, false),
                },
                selected: selectedKVPairKey === kvPair.key,
            };
        });
    }

    return undefined;
};


/**
 * Create a list of Interval options to be used for the Log component Interval Select dropdown
 */
export const createLogRequestIntervalOptions = (trans: TFunction): readonly SelectInputOption<number>[] => {
    const intervals = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    return intervals.map((interval: number) => {
        return {
            label: capitalizeAll(trans('label.value_unit_display', { value: interval, unit: trans(TimeUnit.HOUR, { count: interval }) })),
            value: interval * Time.HOUR_IN_MS,
        };
    });
};



/**
 * Get the display value for a memory size value. For example, if value is 1024, the return value will be 1KB. If value is 2,000,000, the returned
 * value will be 1.9 MBs.
 *
 * @param value
 * @param trans
 * @param abbrev
 * @param decimalPlaces
 * @returns
 */
export const getMemorySizeDisplayValue = (value: number | undefined, trans: TFunction, abbrev: boolean = true, decimalPlaces: number = 2): string => {
    if (value === undefined) {
        return capitalizeAll(trans('label.none'));
    }

    if (value > MemorySize.TERABYTE) {
        const convertedValue = value / MemorySize.TERABYTE;
        return trans('label.value_unit_display', {
            value: roundValue(convertedValue, decimalPlaces),
            unit : capitalizeFirst(trans(abbrev ? MemorySizeUnit.TERABYTE_ABBREV : MemorySizeUnit.TERABYTE, { count: convertedValue })),
        });
    }

    if (value > MemorySize.GIGABYTE) {
        const convertedValue = value / MemorySize.GIGABYTE;
        return trans('label.value_unit_display', {
            value: roundValue(convertedValue, decimalPlaces),
            unit : capitalizeFirst(trans(abbrev ? MemorySizeUnit.GIGABYTE_ABBREV : MemorySizeUnit.GIGABYTE, { count: convertedValue })),
        });
    }

    if (value > MemorySize.MEGABYTE) {
        const convertedValue = value / MemorySize.MEGABYTE;
        return trans('label.value_unit_display', {
            value: roundValue(convertedValue, decimalPlaces),
            unit : capitalizeFirst(trans(abbrev ? MemorySizeUnit.MEGABYTE_ABBREV : MemorySizeUnit.MEGABYTE, { count: convertedValue })),
        });
    }

    if (value > MemorySize.KILOBYTE) {
        const convertedValue = value / MemorySize.KILOBYTE;
        return trans('label.value_unit_display', {
            value: roundValue(convertedValue, decimalPlaces),
            unit : capitalizeFirst(trans(abbrev ? MemorySizeUnit.KILOBYTE_ABBREV : MemorySizeUnit.KILOBYTE, { count: convertedValue })),
        });
    }

    return trans('label.value_unit_display', {
        value: roundValue(value, decimalPlaces),
        unit : capitalizeFirst(trans(abbrev ? MemorySizeUnit.BYTE_ABBREV : MemorySizeUnit.BYTE, { count: value })),
    });
};
